import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, UserCheck, MapPinOff, Hand } from "lucide-react";
import Reveal from "./Reveal";
import Doodle from "./Doodle";
import Highlighter from "./Highlighter";

// Act 4 of the story site: the proof.
//
// This is the section both reference sites are thin on and we are not, so it is
// the one place the page should out-argue them rather than borrow. PLY and
// Timeleft both gesture at safety; we can be specific, because the product
// actually does these things.
//
// Content mirrors the /safety page and the /support FAQ so there is one story
// across the site. No numbers, no testimonials, no "trusted by" claims: we do
// not have consented member quotes yet (content/home-sections.ts marks the
// testimonial set HELD, "never ship fabricated quotes"), and inventing social
// proof on a safety section would be the worst possible place to do it.

const PILLARS = [
  {
    Icon: UserCheck,
    title: "Real names, real faces",
    body: "No anonymous handles. You see who is already looped in before you say yes.",
  },
  {
    Icon: MapPinOff,
    title: "Privacy zones",
    body: "Your exact home and work never show. Neighbourhood level only.",
  },
  {
    Icon: Hand,
    title: "Mute, block, report",
    body: "One tap, from any profile or loop. Reports reach a human within 24 hours.",
  },
  {
    Icon: ShieldCheck,
    title: "Visible host history",
    body: "See what someone has hosted before, so turning up is never a leap of faith.",
  },
];

const TrustAct: React.FC = () => (
  <section className="relative w-full bg-paper-salmon py-24 md:py-32 px-6 overflow-hidden">
    <Doodle
      name="heart"
      size={54}
      rotate={-10}
      className="absolute w-8 h-8 md:w-auto md:h-auto left-[6%] top-[14%] text-salmon-dark/45"
    />
    <Doodle
      name="burst"
      size={40}
      className="absolute w-8 h-8 md:w-auto md:h-auto right-[9%] top-[22%] text-salmon/70"
    />
    <Doodle
      name="loop"
      size={80}
      rotate={12}
      className="absolute w-8 h-8 md:w-auto md:h-auto right-[4%] max-md:right-[5%] bottom-[10%] text-teal/25"
    />

    <div className="max-w-5xl mx-auto relative">
      <div className="max-w-2xl mb-14 md:ml-2">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-salmon-dark mb-4">
          Non negotiable
        </p>
        <Reveal y={20}>
          <h2
            className="font-headline text-ink tracking-[-0.025em] leading-[1.16] text-[clamp(2rem,5.8vw,3.6rem)] text-balance"
            style={{ fontWeight: 700 }}
          >
            Meeting strangers only works if{" "}
            <Highlighter tone="ink">nobody is a stranger to us.</Highlighter>
          </h2>
        </Reveal>
        <Reveal y={14} delay={110}>
          <p className="mt-5 text-lg text-ink/70 leading-relaxed">
            Identity is the trust layer. It is built into the first screen, not
            bolted on after something goes wrong.
          </p>
        </Reveal>
      </div>

      <ul className="grid gap-4 sm:grid-cols-2 list-none">
        {PILLARS.map((p, i) => {
          const { Icon } = p;
          return (
            <Reveal key={p.title} as="li" y={22} delay={i * 90}>
              <div className="h-full bg-canvas rounded-3xl p-6 md:p-7 border border-hairline/50">
                <Icon
                  size={24}
                  className="text-salmon-dark mb-4"
                  aria-hidden="true"
                />
                <h3
                  className="font-headline text-ink text-xl md:text-2xl tracking-[-0.02em] mb-2"
                  style={{ fontWeight: 700 }}
                >
                  {p.title}
                </h3>
                <p className="text-ink/70 leading-relaxed">{p.body}</p>
              </div>
            </Reveal>
          );
        })}
      </ul>

      <Reveal y={14} delay={140}>
        <p className="mt-10 text-ink/60 md:ml-2">
          The full picture is on our{" "}
          <Link
            to="/safety"
            className="text-salmon-dark font-semibold border-b-2 border-salmon/40 hover:border-salmon transition-colors"
          >
            safety page
          </Link>
          .
        </p>
      </Reveal>
    </div>
  </section>
);

export default TrustAct;
